import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  getRouteApi,
  notFound,
  Outlet,
  useRouter,
} from "@tanstack/react-router"
import { useMemo } from "react"
import * as z from "zod"

import type { ExtendedResourceInvoice } from "@repo/supabase/database-types"
import { useUser } from "@repo/supabase/hooks/use-user"
import { Button } from "@repo/ui/components/ui/button"
import { If } from "@repo/ui/components/ui/if"
import { PageDetailsPanel } from "@repo/ui/components/ui/page-details-panel"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { getPersonalAccountQueryOptions } from "~/modules/personal-account/personal-account.queries"
import { ResourceInvoicesSearchList } from "~/modules/resources/components/resource-invoices-search-list"
import { useCreateResourceInvoice } from "~/modules/resources/hooks/use-create-resource-invoice"
import { getExtendedResourceInvoicesQueryOptions } from "~/modules/resources/resource-invoices.queries"
import { FALLBACK_RESOURCE_INVOICE_CURRENCY } from "~/modules/resources/resources.constants"
import { usePermissionMethods } from "~/modules/roles-and-permissions/hooks/use-permission-methods"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const log = logger.child({
  module: "my-invoices-route",
})

const searchParamsSchema = z.object({
  be_slug: z.string().optional(),
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/my-invoices",
)({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
  beforeLoad: async ({ location }) => {
    const [_, __, invoiceId] = location.pathname.split("/")

    return {
      invoiceId: invoiceId || null,
    }
  },
  loaderDeps: ({ search }) => pick(search, ["be_slug"]),
  loader: async ({ context, deps }) => {
    context.queryClient
      .ensureQueryData(getExtendedBusinessEntitiesQueryOptions())
      .catch((error) => {
        log.error("Error getting extended business entities", { error })
        return null
      })
    context.queryClient
      .ensureQueryData(getPersonalAccountQueryOptions(context.user?.id ?? ""))
      .catch((error) => {
        log.error("Error getting personal account", { error })
        return null
      })

    if (context.user && !deps.be_slug) {
      context.queryClient
        .ensureQueryData(
          getExtendedResourceInvoicesQueryOptions({
            resourceUnitId: context.user.id, // A resource's unit ID is the same as their user's ID
          }),
        )
        .catch((error) => {
          log.error("Error getting extended resource invoices", { error })
          return null
        })
    }

    return {
      invoiceId: context.invoiceId,
    }
  },
})

function RouteComponent() {
  const router = useRouter()
  const { be_slug } = Route.useSearch()
  const { invoiceId } = Route.useLoaderData()
  const { isResourceInvoicesOn } = useFeatureConfigs()

  const { data: businessEntitiesData } = useSuspenseQuery(
    getExtendedBusinessEntitiesQueryOptions(),
  )

  const routeContext = getRouteApi("__root__").useRouteContext()
  const { data: user } = useUser({ initialData: routeContext.user })
  const { data: personalAccount } = useSuspenseQuery(
    getPersonalAccountQueryOptions(user?.id ?? ""),
  )

  const selectedBusinessEntity = useMemo(() => {
    const selected = businessEntitiesData.businessEntities.find(
      (entity) => entity.slug === be_slug,
    )

    if (selected) {
      return selected
    }

    return businessEntitiesData.businessEntities[0]
  }, [businessEntitiesData, be_slug])

  const businessEntityFilterOptions = useMemo(() => {
    return businessEntitiesData.businessEntities.map((entity) => ({
      label: entity.name,
      value: entity.slug,
    }))
  }, [businessEntitiesData])

  const { data: resourceInvoicesData, isLoading: isResourceInvoicesLoading } =
    useQuery(
      getExtendedResourceInvoicesQueryOptions({
        resourceUnitId: personalAccount?.unit_id,
        filters: {
          page: 1,
          page_size: 1000, // TODO: This should be replaced with an infinite scroll list (SearchList needs to be updated to support this)
          columnFilters: selectedBusinessEntity?.unit_id
            ? [
                {
                  id: "business_entity_unit_ids",
                  value: [selectedBusinessEntity.unit_id],
                },
              ]
            : undefined,
        },
      }),
    )

  const { permissionMethods, isLoading: isPermissionMethodsLoading } =
    usePermissionMethods()

  const canCreateResourceInvoices = permissionMethods?.hasUnitTypePermission(
    "business_entity",
    "resource_invoice:create",
  )

  const isMobile = useIsMobile()

  if (
    !isResourceInvoicesOn ||
    !personalAccount ||
    !(
      !isPermissionMethodsLoading &&
      (canCreateResourceInvoices ||
        permissionMethods?.getIsResourceSystemUser())
    )
  ) {
    throw notFound()
  }

  const onSelectBusinessEntity = (businessEntitySlug: string | null) => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        be_slug: businessEntitySlug ?? undefined,
      }),
    })
  }

  const onSelectResourceInvoice = (invoice: ExtendedResourceInvoice) => {
    router.navigate({
      to: "/my-invoices/$invoiceId",
      params: { invoiceId: invoice.id },
      search: (prev) => prev,
    })
  }

  const createResourceInvoiceMutation = useCreateResourceInvoice({
    resourceUnitId: personalAccount.unit_id,
    onSuccess: (newInvoiceId) => {
      router.navigate({
        to: "/my-invoices/$invoiceId",
        params: { invoiceId: newInvoiceId },
      })
    },
  })

  const onAddResourceInvoice = () => {
    createResourceInvoiceMutation.mutate({
      businessEntityUnitId: selectedBusinessEntity.unit_id,
      currencyCode:
        selectedBusinessEntity.default_currency ??
        FALLBACK_RESOURCE_INVOICE_CURRENCY,
    })
  }

  return (
    <PagePanel title="My Invoices">
      <div className="flex h-full flex-col gap-4 md:flex-row">
        <div
          className={cn("block md:block md:w-1/3 lg:w-1/4", {
            hidden: isMobile && invoiceId,
          })}
        >
          <ResourceInvoicesSearchList
            resourceInvoices={resourceInvoicesData?.invoices || []}
            selectedResourceInvoiceId={invoiceId}
            onSelectResourceInvoice={onSelectResourceInvoice}
            onAddResourceInvoice={onAddResourceInvoice}
            filterOptions={businessEntityFilterOptions}
            filterValue={selectedBusinessEntity?.slug ?? null}
            setFilterValue={onSelectBusinessEntity}
            filterPlaceholder="Select a business entity"
            isLoading={isResourceInvoicesLoading || isPermissionMethodsLoading}
          />
        </div>

        <div
          className={cn("block flex-1 md:w-2/3 lg:w-3/4", {
            hidden: isMobile,
          })}
        >
          <div className="h-full flex-1 overflow-auto">
            <If
              condition={invoiceId}
              fallback={
                <PageDetailsPanel
                  title="No invoice selected"
                  actions={
                    <Button
                      onClick={onAddResourceInvoice}
                      className="pointer-events-none opacity-0"
                    >
                      Create Invoice
                    </Button>
                  }
                >
                  <div className="flex h-full items-center justify-center">
                    {canCreateResourceInvoices ? (
                      <p>Select or create an invoice</p>
                    ) : (
                      <p>Select an invoice to view</p>
                    )}
                  </div>
                </PageDetailsPanel>
              }
            >
              <Outlet />
            </If>
          </div>
        </div>
      </div>
    </PagePanel>
  )
}
